import React from "react";
import useTranslation from "../../../../../hooks/i18n/useTranslation";
import IconsEnum from "../../../../../classes/enums/IconsEnum";
import Button from "../../../../../components/button/Button";
import IconButton from "../../../../../components/iconButton/IconButton";
import WidgetBox from "../../../../../components/widgetBox/WidgetBox";

import { ButtonSize } from "../../../../../classes/enums/ButtonEnum";
import { ActNatures } from "../../../../../classes/enums/SurgicalActsEnum";
import { generateSurgicalActKey } from "../../../../../classes/utils/SurgeryUtils";
import "../History.css";

const SegmentalTransformationItem = ({
  trans,
  onDelete,
  onEdit,
  isSelected,
  onToggleSelect,
  disabled,
  showCheckbox = true,
  showActions = true,
}) => {
  const { t } = useTranslation();
  return (
    <div className={`history-item ${isSelected ? "selected" : ""}`}>
      <div className="history-row">
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(trans)}
            disabled={disabled}
            className="history-item-checkbox"
          />
        )}
        <div className="App-text-medium">{trans.vertebra}</div>
        <div className="App-text-medium">{trans.type}</div>
        {showActions && (
          <div className="interaction-buttons">
            <IconButton
              icon={IconsEnum.EDIT}
              size={ButtonSize.SMALL}
              isCircle={true}
              noBgColor
              onClick={() => onEdit()}
              disabled={disabled}
              title={t("208")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const SpondyHistory = ({
  onDeleteSurgicalAct,
  onBulkDeleteSurgicalActs,
  onEditSurgicalAct,
  currentSurgicalActs,
  comparedSurgicalActs = [],
  compareMode = false,
  currentPlanLabel = "Plan 1",
  comparedPlanLabel = "Plan 2",
}) => {
  const [isLoading, setLoading] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState(new Set());

  const getActsArray = actsSource => {
    if (!actsSource) {
      return [];
    }
    return Array.isArray(actsSource) ? actsSource : Object.values(actsSource);
  };

  const surgicalActsArray = getActsArray(currentSurgicalActs);
  const comparedActsArray = getActsArray(comparedSurgicalActs);

  // Filter only the acts that are segmental transformations
  const transActs = surgicalActsArray.filter(
    act => act.actNature === ActNatures.SegmentalTransformation
  );
  const comparedTransActs = comparedActsArray.filter(
    act => act.actNature === ActNatures.SegmentalTransformation
  );

  const handleDeleteSegmental = async transToDelete => {
    try {
      if (isLoading) {
        return;
      }

      setLoading(true);
      if (onDeleteSurgicalAct) {
        await onDeleteSurgicalAct(transToDelete);
      } else {
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (isLoading || selectedItems.size === 0) {
        return;
      }

      setLoading(true);

      const selectedTransformations = transActs.filter(act =>
        selectedItems.has(generateSurgicalActKey(act))
      );

      if (onBulkDeleteSurgicalActs) {
        await onBulkDeleteSurgicalActs(selectedTransformations);
        // Clear selection after successful delete
        setSelectedItems(new Set());
      } else {
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleToggleSelect = trans => {
    const key = generateSurgicalActKey(trans);
    const newSelected = new Set(selectedItems);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === transActs.length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all
      const allKeys = new Set(transActs.map(generateSurgicalActKey));
      setSelectedItems(allKeys);
    }
  };

  const editSegmental = trans => {
    if (onEditSurgicalAct) {
      onEditSurgicalAct(trans);
    } else {
    }
  };

  if (transActs.length === 0 && !compareMode) {
    return null;
  }

  const hasSelection = selectedItems.size > 0;
  const isAllSelected = selectedItems.size === transActs.length;

  const renderPlanSection = (label, items) => (
    <div className="history-compare-plan">
      <div className="history-compare-plan-title App-text-medium">
        {label}
      </div>
      {items.length === 0 ? (
        <div className="history-empty App-text-small">
          No segmental transformations
        </div>
      ) : (
        items.map((act, index) => (
          <SegmentalTransformationItem
            key={`${act.vertebra}-${index}-${act._id || "new"}`}
            trans={act}
            onEdit={() => {}}
            isSelected={false}
            onToggleSelect={() => {}}
            disabled={true}
            showCheckbox={false}
            showActions={false}
          />
        ))
      )}
    </div>
  );

  if (compareMode) {
    if (transActs.length === 0 && comparedTransActs.length === 0) {
      return null;
    }

    return (
      <WidgetBox
        title="Spondylolisthesis"
        isSmall={true}
        isInSideBar={true}
        isExpandable={true}
      >
        <div className="side-widget-history history-compare-wrapper">
          {renderPlanSection(currentPlanLabel, transActs)}
          <div className="history-compare-separator" />
          {renderPlanSection(comparedPlanLabel, comparedTransActs)}
        </div>
      </WidgetBox>
    );
  }

  return (
    <WidgetBox
      title="Spondylolisthesis"
      isSmall={true}
      isInSideBar={true}
      isExpandable={true}
    >
      <div className="side-widget-history">
        {/* Selection controls */}
        <div className="history-selection-controls">
          <label className="select-all-checkbox">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              disabled={isLoading}
            />
            <span className="App-text-small">
              {isAllSelected ? "Deselect All" : "Select All"}
            </span>
          </label>
          {hasSelection && (
            <span className="App-text-small selection-count">
              {selectedItems.size} selected
            </span>
          )}
        </div>

        {/* Items list */}
        {transActs.map((act, index) => (
          <SegmentalTransformationItem
            key={`${act.vertebra}-${index}-${act._id || "new"}`}
            trans={act}
            onDelete={handleDeleteSegmental}
            onEdit={() => {
              editSegmental(act);
            }}
            isSelected={selectedItems.has(generateSurgicalActKey(act))}
            onToggleSelect={handleToggleSelect}
            disabled={isLoading}
          />
        ))}

        {/* Bulk delete button */}
        {hasSelection && (
          <div className="bulk-delete-container">
            <Button
              value={`Delete (${selectedItems.size})`}
              onClick={handleBulkDelete}
              disabled={isLoading}
              className="bulk-delete-button"
            />
          </div>
        )}
      </div>
    </WidgetBox>
  );
};

export default SpondyHistory;
